import { isVraiFaux, cleFigure } from './groupQuestions';
import { qcmConvertible, reponseQcmCourte, estTableau, tableConvertible, besoinTableLibre } from './reponseLibre';
import { colonnesEstimees, DISPO_FULL, DISPO_HALF, GROUPE_PAD } from './grilleReponses';
import { itemLourdFigure, besoinGrilleFigure, CAP_COLONNES_FIGGROUP } from './besoinFigureGroup';
import { hauteurImprimee, payloadExigePleineLargeur, svgExigePleineLargeur } from './svgNaturel';
import { expressionsExigentPleineLargeur, lignesTrous } from './expressions';

/**
 * Composition et pagination print (pipeline DOM-based).
 *
 * Pipeline :
 *   selectedQuestions → groupQuestions() → blocks[]
 *                     → composeSections() → sections[] (full / masonry / single)
 *                     → [mesure DOM dans EvalPrintPage]
 *                     → paginateByHeights() → { pages }
 *                     → renumberBlocks() (in place, écrase exNum)
 *
 * ⚠️ Les fonctions estimateXxx ci-dessous ne servent QU'À deux décisions de
 * LAYOUT (jamais aux budgets de page, qui sont mesurés au pixel dans
 * EvalPrintPage) :
 *   1. densité full/half d'un bloc (getBlockDensity) ;
 *   2. répartition LPT des halves entre les 2 colonnes d'une masonry.
 * L'ancien pipeline 100 % estimation (computeMasonryLayout/buildPage) et son
 * jeu de constantes de page divergent ont été supprimés.
 *
 * Types de sections :
 *   - { type: 'full',    block }              → pleine largeur
 *   - { type: 'masonry', leftCol, rightCol }  → 2 cols de halves
 *   - { type: 'single',  blocks }             → blocs pleine largeur empilés
 */

// ─── Constantes de layout (PAS de géométrie de page ici — cf. pageGeometry.js)

/**
 * Échelle UNIQUE des facteurs de zoom (prof + moteur). Les boutons prof
 * s'arrêtent à 0.7 (plancher de lisibilité : 14px ≈ 10,5pt → 55 % ≈ 5,8pt,
 * illisible pour un élève) ; 0.55 et 0.5 sont réservés au moteur en dernier
 * recours (0.5 conservé exprès : un bloc qui tenait tout juste à 0.5
 * déborderait à 0.55). min(manuel, auto) reste toujours sur un palier.
 */
export const ZOOM_LADDER = [1, 0.85, 0.7, 0.55, 0.5];
export const ZOOM_LADDER_PROF = [1, 0.85, 0.7];

/** Palier immédiatement inférieur de la ladder, ou null si déjà au plancher. */
export const nextLadderDown = (factor) => {
    for (const f of ZOOM_LADDER) {
        if (f < factor - 1e-6) return f;
    }
    return null;
};

/**
 * Résout un réglage keyé par uid en remontant la chaîne de suffixes de split :
 * `g3_a_b` → `g3_a` → `g3`. Les enfants de split dérivent, ne possèdent rien.
 */
export const resolveUidSetting = (map, uid) => {
    if (!map || !uid) return undefined;
    let cur = uid;
    for (;;) {
        if (map.has(cur)) return map.get(cur);
        const cut = cur.lastIndexOf('_');
        if (cut <= 0) return undefined;
        cur = cur.slice(0, cut);
    }
};

/** Racine d'un uid de split (`g3_a_b` → `g3`) — les réglages prof vivent là. */
export const rootUidOf = (uid) => (uid || '').split('_')[0];

const LINE_PX = 22;
const CARD_CHROME = 60;
const LABEL_H = 22;
const CHARS_PER_LINE_HALF = 30;
const CHARS_PER_LINE_FULL = 70;
const MASONRY_GAP = 16;
const FULL_WIDTH_THRESHOLD = 500;
const SAISIE_SHORT_THRESHOLD = 10;


// ─── Helpers ──────────────────────────────────────────────────────────

const stripHtml = (s) => (s || '').replace(/<[^>]*>/g, '');

const hasImageChoice = (payload) =>
    (payload?.contenu?.choix || []).some((c) => c?.type === 'image');

const avgChoixLength = (payload) => {
    const choix = payload?.contenu?.choix || [];
    if (!choix.length) return 0;
    const total = choix.reduce((s, c) => {
        if (typeof c === 'string') return s + c.length;
        if (c?.type === 'expression') return s + (c.config?.contenu || '').length;
        if (c?.type === 'image') return s + 100;
        return s + 20;
    }, 0);
    return total / choix.length;
};

const countLines = (text, charsPerLine) => {
    const len = stripHtml(text).length;
    return Math.max(1, Math.ceil(len / charsPerLine));
};

/**
 * Hauteur approximative cumulée des elements_additionnels (image, expression,
 * numberline, geogebra). La table V/F est ignorée — gérée par PrintVraiFaux.
 * Heuristique grossière mais proche du rendu moyen :
 *   - image / geogebra : 130px
 *   - numberline       : 100px
 *   - expression       : 30px (inline ou bloc, ajoute une ligne environ)
 */
/** Hauteur rendue d'une droite graduée : viewBox 600×96 en pleine largeur
 *  (623 px → ≈ 100 px), 300×96 en demi-colonne (271 px → ≈ 87 px), cf.
 *  renderNumberLine + LargeurBloc. */
const NUMBERLINE_H = (halfWidth) => (halfWidth ? 87 : 100);

/** Hauteur d'UN élément — barèmes historiques d'estimateElementsH, à
 *  l'identique (les scènes compas/rapporteur restent à 0 ici pour ne pas
 *  décaler les décisions full/half existantes). */
const estimateElementH = (el, halfWidth) => {
    if (!el) return 0;
    const type = el.type;
    if (type === 'image') {
        // SVG de générateur : hauteur RÉELLE à la largeur imprimée (taille
        // naturelle plafonnée, cf. svgNaturel.js) + marges ; 130 px n'était
        // qu'un forfait (un cadran 436×436 comptait 130).
        const h = hauteurImprimee(el, halfWidth);
        return h != null ? h + 16 : 130;
    }
    if (type === 'geogebra') return 130;
    if (type === 'numberline') return NUMBERLINE_H(halfWidth);
    if (type === 'expression') return 30;
    return 0;
};

/** Hauteur de la figure PARTAGÉE d'un bloc groupé (comptée une fois au
 *  niveau du groupe) — ici les scènes compas/rapporteur comptent, et une
 *  image est estimée à son plafond CSS (320 px, cf. .ep-figgroup-figure) :
 *  le barème ne sert qu'aux nouveaux blocs, aucun risque de dérive sur
 *  l'existant. */
const estimateFigureH = (el, halfWidth) => {
    if (el?.type === 'compas_interactif' || el?.type === 'rapporteur_interactif') return 130;
    if (el?.type === 'image' || el?.type === 'geogebra') return 280;
    return estimateElementH(el, halfWidth);
};

/** `skipCle` : clé (cf. cleFigure) de la figure partagée du bloc — comptée au
 *  niveau du groupe, pas dans chaque item. */
const estimateElementsH = (payload, halfWidth = false, skipCle = null) => {
    const elements = payload?.elements_additionnels?.elements;
    if (!Array.isArray(elements) || !elements.length) return 0;
    let skipped = false;
    return elements.reduce((s, el) => {
        if (skipCle && !skipped && cleFigure(el) === skipCle) {
            skipped = true;
            return s;
        }
        return s + estimateElementH(el, halfWidth);
    }, 0);
};

// ─── Estimation de hauteur ────────────────────────────────────────────

/** Hauteur des lignes de réponse d'un QCM converti en réponse libre
 *  (axe format) — même barème que les saisies : 1 ligne courte ou bloc. */
const estimateQcmLibreAnswerH = (payload, opts) => {
    if (opts.saisieMode === 'sur_le_cahier') return 0;
    return reponseQcmCourte(payload) ? 28 : 2 * 28 + 10;
};

const estimateQcmSubH = (payload, charsPerLine, opts = {}) => {
    const enonceH = countLines(payload?.texte, charsPerLine) * LINE_PX;
    const elementsH = estimateElementsH(payload, charsPerLine === CHARS_PER_LINE_HALF, opts.skipCle);
    if (opts.libre && qcmConvertible(payload)) {
        return enonceH + elementsH + estimateQcmLibreAnswerH(payload, opts) + 8;
    }
    const choix = payload?.contenu?.choix || [];
    const avgLen = avgChoixLength(payload);
    const cols = charsPerLine === CHARS_PER_LINE_HALF
        ? 2
        : (avgLen < 12 ? 4 : 2);
    const rows = Math.ceil(choix.length / cols);
    // Largeur approximative d'une cellule de choix selon la disposition.
    // Au-delà, le texte wrappe et la ligne devient 52px au lieu de 32.
    const wrapThreshold = cols === 2
        ? (charsPerLine === CHARS_PER_LINE_HALF ? 15 : 30)
        : 15;
    const rowH = avgLen > wrapThreshold ? 52 : 32;
    return enonceH + elementsH + rows * rowH + 8;
};

/** Terme commun des groupes : chrome + label + figure partagée éventuelle
 *  (comptée UNE fois — `optsAvecFigure` pose `skipCle` pour que les items ne
 *  la recomptent pas). */
const baseGroupe = (figurePartagee, charsPerLine) =>
    CARD_CHROME + LABEL_H
    + (figurePartagee
        ? estimateFigureH(figurePartagee.element, charsPerLine === CHARS_PER_LINE_HALF)
        : 0);

const optsAvecFigure = (opts, figurePartagee) =>
    (figurePartagee ? { ...opts, skipCle: figurePartagee.cle } : opts);

const estimateQcmGroupH = (items, charsPerLine, opts, figurePartagee = null) => {
    const o = optsAvecFigure(opts, figurePartagee);
    return baseGroupe(figurePartagee, charsPerLine)
        + items.reduce((h, { payload }) => h + estimateQcmSubH(payload, charsPerLine, o), 0);
};

/** Hauteur d'une sub-question saisie groupée (énoncé + lignes éventuelles). */
const estimateSaisieSubH = (payload, charsPerLine, opts = {}) => {
    const enonceH = countLines(payload?.texte, charsPerLine) * LINE_PX;
    const elementsH = estimateElementsH(payload, charsPerLine === CHARS_PER_LINE_HALF, opts.skipCle);
    const surFeuille = opts.saisieMode !== 'sur_le_cahier';
    const ans = payload?.contenu?.bonne_reponse;
    const isShort = ans != null && String(ans).length <= SAISIE_SHORT_THRESHOLD;
    const answerH = !surFeuille ? 0 : (isShort ? 28 : 2 * 28 + 10);
    return enonceH + elementsH + answerH + 8;
};

const estimateSaisieGroupH = (items, charsPerLine, opts, figurePartagee = null) => {
    const o = optsAvecFigure(opts, figurePartagee);
    return baseGroupe(figurePartagee, charsPerLine)
        + items.reduce((h, { payload }) => h + estimateSaisieSubH(payload, charsPerLine, o), 0);
};

/** Hauteur du corps d'un exercice-tableau (V/F ou colonnes nommées), hors
 *  énoncé : tableau à cocher (header ≈ 44px + 50px par ligne, calibré DOM) ou,
 *  en réponse libre (axe format), liste à lignes pointillées (≈ 34px par
 *  ligne, + 28px de justification par affirmation d'un vrai V/F ; cahier :
 *  liste nue ≈ 24px par ligne). */
const estimateTableBodyH = (payload, opts = {}, charsPerLine = CHARS_PER_LINE_FULL, enGroupe = false) => {
    const cahier = opts.saisieMode === 'sur_le_cahier';
    if ((opts.libre || cahier) && tableConvertible(payload)) {
        // Liste sur grille : colonnes approchées comme la grille CSS les
        // choisira (place nominale demi/pleine, zoom du bloc, plafond 2/4).
        const { n, vraiFaux, besoin } = besoinTableLibre(payload, { sansLigne: cahier });
        const half = charsPerLine === CHARS_PER_LINE_HALF;
        const dispo = (half ? DISPO_HALF : DISPO_FULL) - (enGroupe ? GROUPE_PAD : 0);
        const k = vraiFaux && !cahier
            ? 1
            : colonnesEstimees({ besoin, dispo, zoom: opts.zoom ?? 1, cap: half ? 2 : 4 });
        if (cahier) return Math.ceil(n / k) * 24;
        return Math.ceil(n / k) * 34 + (vraiFaux ? n * 28 : 0);
    }
    const n = besoinTableLibre(payload).n;
    return 44 + n * 50;
};

/** Hauteur d'une sub tableau groupée (question + corps + autres éléments). */
const estimateVfSubH = (payload, charsPerLine, opts = {}) => {
    const enonceH = countLines(payload?.texte, charsPerLine) * LINE_PX;
    const elementsH = estimateElementsH(payload, charsPerLine === CHARS_PER_LINE_HALF, opts.skipCle);
    return enonceH + elementsH + estimateTableBodyH(payload, opts, charsPerLine, true) + 8;
};

const estimateVfGroupH = (items, charsPerLine, opts, figurePartagee = null) => {
    const o = optsAvecFigure(opts, figurePartagee);
    return baseGroupe(figurePartagee, charsPerLine)
        + items.reduce((h, { payload }) => h + estimateVfSubH(payload, charsPerLine, o), 0);
};

/** Hauteur d'un exercice d'association (relier / ordonner / classer / texte_trou). */
const estimateAssociationH = (payload, charsPerLine) => {
    const enonceH = countLines(payload?.texte, charsPerLine) * LINE_PX;
    const c = payload?.contenu || {};
    const base = CARD_CHROME + LABEL_H + enonceH;
    if (c.subType === 'relier') {
        const pairs = c.pairs || [];
        const leurres = c.cibles_leurres || [];
        // La colonne la plus haute dicte la hauteur : avec des cibles leurres,
        // la colonne droite compte pairs.length + leurres.length cellules.
        const n = Math.max(pairs.length, pairs.length + leurres.length);
        // En half-col (~110px par cellule), du texte > 15 chars wrappe sur 2 lignes.
        // On détecte le plus long source/target/leurre pour anticiper la hauteur.
        const maxLen = Math.max(
            pairs.reduce(
                (m, p) => Math.max(m, (p.source || '').length, (p.target || '').length),
                0
            ),
            leurres.reduce((m, l) => Math.max(m, (l.target || '').length), 0),
        );
        const wrapsInHalf = charsPerLine === CHARS_PER_LINE_HALF && maxLen > 15;
        const cellH = wrapsInHalf ? 56 : 40;  // 2 lignes vs 1 ligne
        return base + n * cellH + Math.max(0, n - 1) * 8 + 8;
    }
    if (c.subType === 'ordonner') {
        // Items en ligne séparés par `•` — ~6 items/ligne en half-col, ~12 en full-col
        const n = (c.items || []).length;
        const perLine = charsPerLine === CHARS_PER_LINE_HALF ? 6 : 12;
        const lines = Math.max(1, Math.ceil(n / perLine));
        return base + lines * 28 + 8;
    }
    if (c.subType === 'classer') {
        // Ligne d'items ≈ 28px (1 ligne en demi-col avec chips séparés par `•`),
        // en-tête tableau ≈ 36px, cellule vide haute fixée à 80px.
        // Si beaucoup d'items (> ~8 en half-col), la ligne d'items wrappe → +28px.
        const n = (c.categories || []).reduce((s, cat) => s + (cat.items || []).length, 0);
        const itemsLines = Math.max(1, Math.ceil(n / 6));
        return base + itemsLines * 28 + 36 + 80;
    }
    if (c.subType === 'texte_trou') {
        // Phrase multi-lignes KaTeX (une égalité $...$ par \n, ex. gen_1005) :
        // chaque ligne s'imprime en bloc (cf. .ep-tt-ligne) et une fraction
        // \dfrac est haute — countLines (chars/ligne) sous-estimait d'un
        // facteur 3 et faisait déborder la page.
        const lignesMath = String(c.phrase || '').split('\n')
            .filter((l) => { const t = l.trim(); return t.startsWith('$') && t.endsWith('$'); });
        // Mode classique : chaque \n force un retour à la ligne à l'impression
        // (PrintAssociation, 2026-09-02) → lignes comptées \n par \n, sinon
        // une série de 4 égalités était estimée sur une seule ligne.
        const phraseH = lignesMath.length > 0
            ? lignesMath.length * 56    /* \dfrac display ≈ 48px + marge */
            : lignesTrous(c.phrase)
                .reduce((s, l) => s + countLines(l, charsPerLine), 0) * 34;  /* line-height 1.9 ≈ 34px */
        const banqueH = c.mode === 'drag' ? 40 : 0;
        return base + phraseH + banqueH;
    }
    if (c.subType === 'placer_points') {
        // Droite graduée : ≈ 100 px en pleine largeur, ≈ 87 px en demi
        // (viewBox adapté à la largeur du bloc, cf. renderNumberLine)
        const labelsH = (c.pointsToPlace || []).length > 0 ? 22 : 0;
        return base + labelsH + NUMBERLINE_H(charsPerLine === CHARS_PER_LINE_HALF) + 8;
    }
    if (c.subType === 'placer_points_2d') {
        // Repère SVG 600×412 scaled à 100% de largeur du parent (~500px en
        // full-col) → height ≈ (412/600) × 500 ≈ 343 (toujours full, cf. densité)
        const labelsH = (c.pointsToPlace || []).length > 0 ? 22 : 0;
        return base + labelsH + 343 + 8;
    }

    if (c.subType === 'programme_construction') {
        const nTasks = (c.tasks || []).length;
        // ~1 tâche par ligne (consigne rich text), chip + texte
        const tasksH = nTasks * 34;
        // Figure SVG 600×400 scaled à 100% de largeur du parent (~500px en full-col)
        // → height proportionnel ≈ (400/600) * 500 ≈ 333, plus petit en half-col
        const figureH = charsPerLine === CHARS_PER_LINE_HALF ? 200 : 333;
        return base + tasksH + figureH + 16;
    }
    if (c.subType === 'placer_aiguilles') {
        // Cadran vierge fixe 180×180 + mention trotteuse eventuelle
        return base + (c.avecTrotteuse ? 20 : 0) + 190 + 8;
    }
    // Sous-type inconnu — garde-fou
    return base + 120;
};

/** Hauteur d'un item d'un bloc mixte à figure partagée ('figure-group') —
 *  corps par type (mêmes barèmes que les subs), consigne d'item non comprise. */
const estimateFigureItemH = (payload, charsPerLine, opts = {}) => {
    if (payload?.type === 'association') {
        // estimateAssociationH inclut chrome + label (bloc single) : retirés
        // ici, l'item vit dans le groupe. La mesure DOM corrige le reste.
        return Math.max(40, estimateAssociationH(payload, charsPerLine) - CARD_CHROME - LABEL_H) + 8;
    }
    if (estTableau(payload)) return estimateVfSubH(payload, charsPerLine, opts);
    if (payload?.type === 'texte') return estimateSaisieSubH(payload, charsPerLine, opts);
    if (payload?.type === 'qcm') return estimateQcmSubH(payload, charsPerLine, opts);
    return 60;
};

const estimateFigureGroupH = (block, charsPerLine, opts = {}) => {
    const fig = block?.figurePartagee || null;
    const o = optsAvecFigure(opts, fig);
    const items = block?.items || [];
    // Grille des sous-questions : les légères se placent jusqu'à 2 par
    // rangée quand la place réelle le permet (miroir de .ep-figgroup-grid),
    // les lourdes occupent leur rangée. Pas de consigne par item.
    const half = charsPerLine === CHARS_PER_LINE_HALF;
    const legers = [];
    let hLourds = 0;
    for (const { payload } of items) {
        const h = estimateFigureItemH(payload, charsPerLine, o);
        if (itemLourdFigure(payload)) hLourds += h;
        else legers.push(h);
    }
    let hLegers = legers.reduce((s, h) => s + h, 0);
    if (legers.length > 1) {
        const k = colonnesEstimees({
            besoin: besoinGrilleFigure(items),
            dispo: (half ? DISPO_HALF : DISPO_FULL) - GROUPE_PAD,
            zoom: o.zoom ?? 1,
            cap: CAP_COLONNES_FIGGROUP,
        });
        // 2 colonnes → la hauteur des rangées ≈ somme / k, majorée du plus
        // haut item (rangées inégales). La mesure DOM corrige derrière.
        if (k > 1) hLegers = Math.max(Math.ceil(hLegers / k), Math.max(...legers));
    }
    return baseGroupe(fig, charsPerLine) + hLourds + hLegers;
};

const estimateSingleH = (payload, charsPerLine, opts = {}) => {
    const enonceH = countLines(payload?.texte, charsPerLine) * LINE_PX;
    const elementsH = estimateElementsH(payload, charsPerLine === CHARS_PER_LINE_HALF);
    if (payload?.type === 'association') {
        // estimateAssociationH gère déjà ses propres éléments (numberline, figure…)
        return estimateAssociationH(payload, charsPerLine);
    }
    if (estTableau(payload)) {
        // V/F ET tableaux à colonnes nommées (avant : seuls les V/F, un
        // colonnesLibres tombait dans la branche QCM avec 0 choix → tableau
        // invisible pour l'estimateur).
        return CARD_CHROME + LABEL_H + enonceH + elementsH + estimateTableBodyH(payload, opts, charsPerLine, false);
    }
    if (payload?.type === 'texte') {
        const surFeuille = opts.saisieMode !== 'sur_le_cahier';
        const ans = payload?.contenu?.bonne_reponse;
        const isShort = ans != null && String(ans).length <= SAISIE_SHORT_THRESHOLD;
        const answerH = !surFeuille ? 0 : (isShort ? 28 : 2 * 28 + 10);
        return CARD_CHROME + LABEL_H + enonceH + elementsH + answerH;
    }
    if (payload?.type === 'qcm') {
        if (opts.libre && qcmConvertible(payload)) {
            return CARD_CHROME + LABEL_H + enonceH + elementsH + estimateQcmLibreAnswerH(payload, opts);
        }
        const choix = payload?.contenu?.choix || [];
        const avgLen = avgChoixLength(payload);
        const cols = charsPerLine === CHARS_PER_LINE_HALF
            ? 2
            : (avgLen < 12 ? 4 : 2);
        const rows = Math.ceil(choix.length / cols);
        const wrapThreshold = cols === 2
            ? (charsPerLine === CHARS_PER_LINE_HALF ? 15 : 30)
            : 15;
        const rowH = avgLen > wrapThreshold ? 52 : 32;
        return CARD_CHROME + LABEL_H + enonceH + elementsH + rows * rowH;
    }
    return CARD_CHROME + LABEL_H + enonceH + elementsH + 120;
};

const estimateBlockHeight = (block, halfWidth = true, opts = {}) => {
    const chars = halfWidth ? CHARS_PER_LINE_HALF : CHARS_PER_LINE_FULL;
    // Axe format : QCM rendu en réponse libre (résolu par uid par l'appelant)
    const libre = opts.reponseFor ? opts.reponseFor(block?.uid) === 'libre' : false;
    const zoomBloc = opts.zoomFor ? opts.zoomFor(block) : 1;
    const o = (libre || zoomBloc < 1) ? { ...opts, libre, zoom: zoomBloc } : opts;
    let h;
    if (block?.kind === 'qcm-group')         h = estimateQcmGroupH(block.items || [], chars, o, block.figurePartagee);
    else if (block?.kind === 'saisie-group') h = estimateSaisieGroupH(block.items || [], chars, o, block.figurePartagee);
    else if (block?.kind === 'vf-group')     h = estimateVfGroupH(block.items || [], chars, o, block.figurePartagee);
    else if (block?.kind === 'figure-group') h = estimateFigureGroupH(block, chars, o);
    else h = estimateSingleH(block?.payload, chars, o);
    // Facteur de zoom effectif (manuel/auto) : l'estimation suit grossièrement —
    // la vérité vient de rebalanceMasonryFromDom qui mesure le DOM zoomé.
    const zoom = opts.zoomFor ? opts.zoomFor(block) : 1;
    return zoom < 1 ? h * zoom : h;
};

// ─── Densité (full vs half) ───────────────────────────────────────────

const getBlockDensity = (block, opts = {}) => {
    // Forçage prof (résolu + filtré des refus par l'appelant) : prioritaire sur
    // TOUTES les règles, y compris les « toujours full » pédagogiques — c'est
    // un opt-in explicite, assumé via l'avertissement des contrôles.
    const forced = opts.forcedDensityFor ? opts.forcedDensityFor(block?.uid) : null;
    if (forced === 'full' || forced === 'half') return forced;
    // SVG large (> 340 px) ou à petites étiquettes : en demi-colonne il serait
    // réduit sous ×0,8 ou ses polices sous 8 px (illisible sur papier, cf.
    // svgNaturel.js) → pleine largeur, quel que soit le type de bloc.
    const payloads = block?.kind === 'single'
        ? [block.payload]
        : (block?.items || []).map((it) => it?.payload);
    if (payloads.some(payloadExigePleineLargeur)) return 'full';
    if (block?.figurePartagee?.element && svgExigePleineLargeur(block.figurePartagee.element)) return 'full';
    // Expression insécable (« D = 48 − 8 × 10 ÷ 4 », formule KaTeX, ligne à
    // trous) plus large que la demi-colonne → pleine largeur (expressions.js).
    if (payloads.some(expressionsExigentPleineLargeur)) return 'full';
    if (block?.kind === 'qcm-group') {
        const items = block.items || [];
        if (items.length > 4) return 'full';
        if (items.some(({ payload }) => hasImageChoice(payload))) return 'full';
    } else if (block?.kind === 'saisie-group') {
        if ((block.items || []).length > 4) return 'full';
    } else if (block?.kind === 'vf-group') {
        // Chaque V/F embarque un tableau → dès 2 subs, pleine largeur.
        // En réponse libre (axe format), ce sont des listes : la hauteur décide.
        const libre = opts.reponseFor ? opts.reponseFor(block?.uid) === 'libre' : false;
        if (!libre && (block.items || []).length >= 2) return 'full';
    } else if (block?.kind === 'figure-group') {
        const items = block.items || [];
        if (items.length > 4) return 'full';
        // Sous-types qui exigent la pleine largeur (précision du tracé) —
        // mêmes règles que les singles.
        if (items.some(({ payload }) => {
            const sub = payload?.contenu?.subType;
            return sub === 'programme_construction' || sub === 'placer_points_2d';
        })) return 'full';
        // Un tableau à cocher dans le lot (non libre) → full, comme vf-group.
        const libre = opts.reponseFor ? opts.reponseFor(block?.uid) === 'libre' : false;
        if (!libre && items.some(({ payload }) => estTableau(payload))) return 'full';
    } else if (block?.kind === 'single') {
        const p = block.payload;
        if (p?.type === 'qcm' && !isVraiFaux(p)) {
            const br = p?.contenu?.bonne_reponse;
            if (Array.isArray(br) && br.length > 4) return 'full';
        }
        if (p?.type === 'association') {
            const sub = p?.contenu?.subType;
            // Programme de construction : toujours full (la figure géométrique
            // perd en lisibilité compressée en demi-col)
            if (sub === 'programme_construction') return 'full';
            // Repère 2D : toujours full — l'élève place des points au crayon
            // sur le quadrillage, la précision exige la pleine largeur
            if (sub === 'placer_points_2d') return 'full';
            // placer_points : full si numberline step dense (> 50 graduations)
            // ou beaucoup de points, sinon height-driven
            if (sub === 'placer_points') {
                const nl = p?.contenu?.numberline;
                if (nl && nl.step > 0 && (nl.end - nl.start) / nl.step > 50) return 'full';
                const nPoints = (p?.contenu?.pointsToPlace || []).length;
                if (nPoints > 8) return 'full';
            }
            // ordonner / classer / texte_trou → décision height-driven (ci-dessous)
        }
    }
    return estimateBlockHeight(block, true, opts) > FULL_WIDTH_THRESHOLD ? 'full' : 'half';
};

const isFullBlock = (block, opts = {}) => getBlockDensity(block, opts) === 'full';

// ─── Masonry pure (pas de budget) ─────────────────────────────────────

const buildMasonryFromBlocks = (blocks, opts = {}) => {
    // LPT (Longest Processing Time) : on trie par hauteur décroissante avant
    // distribution. Heuristique classique de bin-packing qui minimise le max
    // des 2 cols. L'ordre de lecture sera corrigé par `renumberBlocks` —
    // l'élève voit toujours 1→2→3 en lisant col gauche puis col droite.
    const sorted = [...blocks].sort(
        (a, b) => estimateBlockHeight(b, true, opts) - estimateBlockHeight(a, true, opts)
    );
    const leftCol = [];
    const rightCol = [];
    let leftH = 0;
    let rightH = 0;
    for (const block of sorted) {
        const h = estimateBlockHeight(block, true, opts);
        if (leftH <= rightH) {
            const gap = leftCol.length > 0 ? MASONRY_GAP : 0;
            leftCol.push(block);
            leftH += gap + h;
        } else {
            const gap = rightCol.length > 0 ? MASONRY_GAP : 0;
            rightCol.push(block);
            rightH += gap + h;
        }
    }
    return { leftCol, rightCol, height: Math.max(leftH, rightH) };
};

// ─── Composition pure (sans pagination) — pour DOM-based pagination ───

/**
 * Compose les blocs en sections (masonry / full / single) **sans** se soucier
 * du budget par page. Sortie : flat array de sections.
 *
 * Utilisé par la pipeline DOM-based pagination : on rend ces sections en
 * mode mesure, on récupère leurs hauteurs réelles, puis on les paginate
 * via `paginateByHeights`.
 *
 * Différence avec `buildPage` (legacy) : ne split pas en pages, n'utilise
 * pas d'estimation de hauteur — laisse le DOM mesurer après render.
 */
/**
 * Max halves dans une masonry section par défaut. Réduit dynamiquement par
 * la boucle de mesure si une masonry dépasse encore le budget.
 */
const DEFAULT_MAX_HALVES_PER_MASONRY = 6;

export const composeSections = (blocks, opts = {}) => {
    if (!blocks.length) return [];

    const maxHalves = opts.maxHalvesPerMasonry || DEFAULT_MAX_HALVES_PER_MASONRY;
    const sections = [];
    let pendingHalves = [];
    // ⚠️ Le quota maxHalves compte TOUS les demi, forcés compris : c'est le
    // seul mécanisme qui borne la taille d'une masonry, donc sa PAGINABILITÉ.
    // (Une v1 exemptait les forcés → « ½ col. pour tous » construisait une
    // section de 2 pages de haut que le rastériseur découpait n'importe où :
    // en-têtes orphelins + pages blanches, constatés sur PDF réel.) Le forçage
    // prof garde son vrai sens ailleurs : jamais rétrogradé en full par les
    // règles de densité (getBlockDensity) — les excédents vont simplement
    // dans la masonry SUIVANTE.

    const flushHalves = () => {
        if (pendingHalves.length === 0) return;
        const m = buildMasonryFromBlocks(pendingHalves, opts);
        const total = m.leftCol.length + m.rightCol.length;
        if (total === 1) {
            sections.push({ type: 'single', blocks: [...m.leftCol, ...m.rightCol] });
        } else if (m.rightCol.length === 0) {
            sections.push({ type: 'single', blocks: m.leftCol });
        } else {
            sections.push({ type: 'masonry', leftCol: m.leftCol, rightCol: m.rightCol });
        }
        pendingHalves = [];
    };

    for (const block of blocks) {
        if (isFullBlock(block, opts)) {
            flushHalves();
            sections.push({ type: 'full', block });
        } else {
            pendingHalves.push(block);
            if (pendingHalves.length >= maxHalves) {
                flushHalves();
            }
        }
    }
    flushHalves();
    return sections;
};

/**
 * Splitte un bloc groupé (qcm-group / saisie-group / vf-group) en 2 sous-blocs
 * de N/2 items chacun. Retourne null si le bloc n'est pas splittable.
 *
 * Les sous-blocs reçoivent un `exNum` temporaire **unique** pour éviter les
 * collisions de clé React pendant les itérations de mesure. Le vrai exNum
 * sera réécrit par `renumberBlocks` après la pagination finale.
 */
let _splitTmpCounter = 0;
export const splitGroupBlock = (block) => {
    if (!block || !Array.isArray(block.items) || block.items.length < 2) return null;
    const splittable = ['qcm-group', 'saisie-group', 'vf-group', 'figure-group'];
    if (!splittable.includes(block.kind)) return null;
    const mid = Math.ceil(block.items.length / 2);
    _splitTmpCounter += 2;
    // uid enfants : `uid_a`/`uid_b` — ils n'ont AUCUN réglage propre, la
    // résolution (resolveUidSetting) remonte au parent. Indispensable : le
    // point de découpe dépend du zoom, une entrée stockée sur `_a`
    // s'appliquerait à un contenu différent d'une itération à l'autre.
    // `figurePartagee` est recopiée dans les DEUX moitiés : chacune ré-affiche
    // la figure (une coupe de colonne/page sans elle serait illisible).
    const figure = block.figurePartagee ? { figurePartagee: block.figurePartagee } : {};
    return [
        {
            kind: block.kind,
            items: block.items.slice(0, mid),
            exNum: `tmp${_splitTmpCounter - 1}`,
            uid: `${block.uid}_a`,
            ...figure,
        },
        {
            kind: block.kind,
            items: block.items.slice(mid),
            exNum: `tmp${_splitTmpCounter}`,
            uid: `${block.uid}_b`,
            ...figure,
        },
    ];
};

const fixForSection = (section, effectiveZoomFor) => {
    const isAtomic =
        section.type === 'full'
        || (section.type === 'single' && section.blocks.length === 1);
    if (isAtomic) {
        const block = section.type === 'full' ? section.block : section.blocks[0];
        const split = splitGroupBlock(block);
        if (split) {
            return { type: 'split', targetBlock: block, replacement: split };
        }
        // Réduction auto : palier suivant de la ladder UNIQUE sous le facteur
        // EFFECTIF (min manuel/auto). Au plancher (0.5) → null : plus rien à
        // faire, paginateByHeights forcera la section seule (comportement de
        // repli historique). L'ancien 0.4 sans classe CSS (no-op silencieux)
        // disparaît.
        const current = effectiveZoomFor ? effectiveZoomFor(block) : 1;
        const nextFactor = nextLadderDown(current);
        if (nextFactor == null) return null;
        return { type: 'scale', targetUid: block.uid, factor: nextFactor };
    }
    return { type: 'reduceMaxHalves' };
};

/**
 * Diagnose **toutes** les sections qui dépassent `budget` et retourne la liste
 * des fixes à appliquer en parallèle. Cruxiale pour la convergence : si N
 * sections débordent, on les traite toutes en 1 itération au lieu de N.
 *
 * @param {Function} effectiveZoomFor  (block) => facteur effectif courant
 * @returns {Array<{type, ...}>} liste de fixes (split, scale, reduceMaxHalves)
 */
export const findAllOversizedFixes = (sections, heights, budget, effectiveZoomFor) => {
    const fixes = [];
    let needsReduceMaxHalves = false;
    for (let i = 0; i < sections.length; i++) {
        if (heights[i] <= budget) continue;
        const fix = fixForSection(sections[i], effectiveZoomFor);
        if (!fix) continue;                    // bloc déjà au plancher : rien à faire
        if (fix.type === 'reduceMaxHalves') {
            needsReduceMaxHalves = true;
        } else {
            fixes.push(fix);
        }
    }
    if (needsReduceMaxHalves) {
        fixes.push({ type: 'reduceMaxHalves' });
    }
    return fixes;
};

/**
 * Helper : remplace `targetBlock` dans `blocks` par `replacement` (array de
 * nouveaux blocs). Préserve l'ordre.
 */
export const replaceBlock = (blocks, targetBlock, replacement) => {
    const idx = blocks.indexOf(targetBlock);
    if (idx === -1) return blocks;
    return [...blocks.slice(0, idx), ...replacement, ...blocks.slice(idx + 1)];
};

/**
 * Rééquilibre une section masonry à partir des hauteurs RÉELLES de ses blocs
 * (lues dans le DOM de la phase mesure). La répartition initiale des colonnes
 * (LPT sur estimations) peut laisser une colonne bien plus courte que l'autre
 * → blanc en bas de colonne. Ici on redistribue en LPT sur les vraies
 * hauteurs, on MUTE section.leftCol/rightCol (même convention in-place que
 * renumberBlocks) et on retourne la nouvelle hauteur de section.
 *
 * @param {object} section     section {type:'masonry', leftCol, rightCol}
 * @param {Element} sectionEl  wrapper DOM mesuré de la section
 * @param {number} gap         gap vertical entre blocs d'une colonne (px)
 * @returns {number|null}      nouvelle hauteur max(colonnes), ou null si le
 *                             DOM ne correspond pas (on garde alors la mesure brute)
 */
export const rebalanceMasonryFromDom = (section, sectionEl, gap = MASONRY_GAP) => {
    if (!section || section.type !== 'masonry' || !sectionEl) return null;
    const cols = sectionEl.querySelectorAll('.ep-masonry > .ep-col');
    if (cols.length !== 2) return null;

    const lireColonne = (colEl, blocks) => {
        const kids = Array.from(colEl.children);
        if (kids.length !== blocks.length) return null;
        return blocks.map((b, k) => ({ block: b, h: kids[k].getBoundingClientRect().height }));
    };
    const gauche = lireColonne(cols[0], section.leftCol);
    const droite = lireColonne(cols[1], section.rightCol);
    if (!gauche || !droite) return null;

    // LPT sur hauteurs réelles
    const tous = [...gauche, ...droite].sort((a, b) => b.h - a.h);
    const L = [];
    const R = [];
    let hL = 0;
    let hR = 0;
    for (const { block, h } of tous) {
        if (hL <= hR) {
            hL += (L.length > 0 ? gap : 0) + h;
            L.push(block);
        } else {
            hR += (R.length > 0 ? gap : 0) + h;
            R.push(block);
        }
    }
    section.leftCol = L;
    section.rightCol = R;
    return Math.max(hL, hR);
};

/**
 * Pagine des sections d'après leurs hauteurs réelles mesurées via
 * `getBoundingClientRect()`. First-fit à FENÊTRE (réordonnancement borné) :
 * quand la section suivante ne tient pas dans le reste de la page, on cherche
 * dans les `lookahead` sections suivantes une section qui bouche le trou —
 * sinon on ouvre une nouvelle page. La numérotation est réécrite ensuite par
 * `renumberBlocks` dans l'ordre visuel, donc l'élève voit toujours 1→2→3.
 *
 * @param {Array} sections      sortie de composeSections
 * @param {Array<number>} heights hauteurs réelles correspondantes (même ordre)
 * @param {number} firstBudget  budget hauteur page 1 (px)
 * @param {number} otherBudget  budget hauteur pages 2+ (px)
 * @param {number} gap          gap entre sections (px, default 16)
 * @param {number} lookahead    taille de la fenêtre de réordonnancement
 * @returns {{ pages: Array<{ sections: Array }> }}
 */
export const paginateByHeights = (sections, heights, firstBudget, otherBudget, gap = 16, lookahead = 4) => {
    if (!sections.length) return { pages: [] };

    const n = sections.length;
    const placed = new Array(n).fill(false);
    let placedCount = 0;

    const pages = [];
    let currentSections = [];
    let usedH = 0;
    let isFirst = true;

    const flushPage = () => {
        if (currentSections.length === 0) return;
        pages.push({ sections: currentSections });
        currentSections = [];
        usedH = 0;
        isFirst = false;
    };

    const place = (idx) => {
        const sectionGap = currentSections.length > 0 ? gap : 0;
        currentSections.push(sections[idx]);
        usedH += sectionGap + (heights[idx] || 0);
        placed[idx] = true;
        placedCount += 1;
    };

    let cursor = 0;
    while (placedCount < n) {
        while (cursor < n && placed[cursor]) cursor += 1;
        if (cursor >= n) break;

        const budget = isFirst ? firstBudget : otherBudget;
        const sectionGap = currentSections.length > 0 ? gap : 0;
        const h = heights[cursor] || 0;

        if (usedH + sectionGap + h <= budget) {
            place(cursor);
            continue;
        }

        if (currentSections.length === 0) {
            // Section seule plus grande que le budget → forcée + warn
            // eslint-disable-next-line no-console
            console.warn(
                `[paginateByHeights] section ${cursor} (${h}px) > budget (${budget}px) — forcée seule`
            );
            place(cursor);
            flushPage();
            continue;
        }

        // Réordonnancement borné : une section proche peut boucher le trou
        let filler = -1;
        const fin = Math.min(n, cursor + 1 + lookahead);
        for (let j = cursor + 1; j < fin; j++) {
            if (placed[j]) continue;
            if (usedH + gap + (heights[j] || 0) <= budget) { filler = j; break; }
        }
        if (filler !== -1) {
            place(filler);
            continue; // re-tente cursor (un autre filler peut encore tenir)
        }
        flushPage();
    }
    flushPage();

    return { pages };
};

// ─── Renumérotage (écrase block.exNum dans l'ordre de lecture) ────────

/**
 * Après placement, on réassigne `exNum` pour que l'élève voie 1→2→3…
 * dans l'ordre visuel (top→bottom, col gauche avant col droite, section
 * après section, page après page). L'ordre original est conservé via
 * `originalIndex` sur chaque bloc/item si besoin de debug.
 */
export const renumberBlocks = (pages) => {
    let n = 1;
    const number = (block) => {
        block.exNum = n;
        n += 1;
    };
    for (const page of pages) {
        for (const section of page.sections) {
            if (section.type === 'full') {
                number(section.block);
            } else if (section.type === 'single') {
                section.blocks.forEach(number);
            } else {
                section.leftCol.forEach(number);
                section.rightCol.forEach(number);
            }
        }
    }
};

